import { Product, SupplierOrderDetail } from 'openapi';
import { useEffect } from 'react';
import { UseFormGetValues, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import type { SaveSemenOrder } from '../index';

interface Props {
  setValue: UseFormSetValue<SaveSemenOrder>;
  watch: UseFormWatch<SaveSemenOrder>;
  getValues: UseFormGetValues<SaveSemenOrder>;
  transformFromOrderDetail: (existingOrder: SupplierOrderDetail, products: Product[]) => Partial<SaveSemenOrder>;
  open: boolean;
  existingOrder: SupplierOrderDetail | undefined;
  products: Product[];
}

/**
 * Small hook to handle/set the mare_customer select based on the order data.
 *
 * In some sutations the customer is not the owner of the mare for an existing order.
 * In such siutaion we should set use the "mareUid" value only and not "mareUid-contactUid".
 *
 * We also watch the mare_customer value to trigger a re-render as the regular watch subscription does
 * not trigger such re-render.
 */
function useSemenOrderSetValueForMareCustomerSelect({
  setValue,
  watch,
  getValues,
  transformFromOrderDetail,
  open,
  existingOrder,
  products,
}: Props): void {
  // we use this value just to trigger a re-render of the useEffect
  // as the regular watch subscription does not trigger such re-render
  const mareCustomerFormValue = watch('mare_customer');

  useEffect(() => {
    if (existingOrder && open) {
      const formFields = transformFromOrderDetail(existingOrder, products);
      // <HorseContactInputSelect /> is helping us here as it will reset the value for the mare_customer
      // when the selected option is not available
      // So by checking both for an undefined value and for a defined value in the formFields
      // we can determine if we should set only the mare_uid for the mare_customer field.
      if (getValues('mare_customer') === undefined && formFields.mare_customer) {
        const [mareUid] = formFields.mare_customer ? formFields.mare_customer.split('-') : [undefined];
        setValue('mare_customer', mareUid);
      }
    }
    // we added mareCustomerFormValue to the dependency array to so it will trigger a re-render
  }, [existingOrder, getValues, mareCustomerFormValue, open, products, setValue, transformFromOrderDetail]);
}

export default useSemenOrderSetValueForMareCustomerSelect;
